import { Card, CardContent, CardHeader, Icon, Button } from 'dreambel-ui';
import { t } from 'i18next';

export interface Feature {
  readonly text: string;
  readonly isAvailable: boolean;
  readonly isComingSoon?: boolean;
}

interface PricingCardProps {
  readonly title: string;
  readonly description: string;
  readonly features: readonly Feature[];
  readonly price?: {
    readonly monthly?: {
      readonly amount: number;
      readonly currency: string;
    };
    readonly annual?: {
      readonly amount: number;
      readonly currency: string;
    };
    readonly lifetime?: {
      readonly amount: number;
      readonly currency: string;
    };
  };
  readonly isHighlighted?: boolean;
  readonly className?: string;
  readonly onDownload?: () => void;
  readonly onSubscribe?: () => void;
  readonly onLifetime?: () => void;
  readonly isAnnual?: boolean;
  readonly onToggleAnnual?: (isAnnual: boolean) => void;
}

export function PricingCard({
  title,
  description,
  features,
  price,
  isHighlighted = false,
  className = '',
  onDownload,
  onSubscribe,
  onLifetime,
  isAnnual = false,
  onToggleAnnual,
}: PricingCardProps) {
  return (
    <Card
      variant={isHighlighted ? 'surface-variant' : 'outlined'}
      elevation="md"
      className={className}
    >
      <CardHeader>
        <h2 className="text-2xl font-semibold text-md-on-surface text-center">
          {title}
        </h2>
        {price && (
          <div className="text-center mt-4">
            {/* Toggle Monthly/Annual pour Premium uniquement */}
            {price && onToggleAnnual && (
              <div className="flex items-center justify-center my-4">
                <div className="bg-md-surface rounded-full p-1 flex items-center">
                  <button
                    className={`px-4 py-2 rounded-full font-medium transition-all text-sm ${
                      !isAnnual
                        ? 'bg-md-secondary-container text-md-on-secondary shadow-sm'
                        : 'text-white shadow-sm'
                    }`}
                    onClick={() => onToggleAnnual(false)}
                  >
                    {t('pricing.toggle.monthly')}
                  </button>
                  <button
                    className={`px-4 py-2 rounded-full font-medium transition-all text-sm relative ${
                      isAnnual
                        ? 'bg-md-secondary-container text-md-on-secondary shadow-sm'
                        : 'text-white shadow-sm'
                    }`}
                    onClick={() => onToggleAnnual(true)}
                  >
                    {t('pricing.toggle.annual')}
                  </button>
                </div>
              </div>
            )}
            {price.monthly && (
              <div className="mb-2">
                <span className="text-6xl font-bold">
                  {isAnnual
                    ? price.annual?.amount ||
                      Math.round(price.monthly.amount * 12 * 0.67)
                    : price.monthly.amount}
                  {price.monthly.currency}
                </span>
                <span className="text-3xl text-gray-300">
                  /
                  {isAnnual
                    ? t('pricing.price.year')
                    : t('pricing.price.month')}
                </span>
              </div>
            )}
            {price.lifetime && (
              <div className="text-lg">
                {t('pricing.price.or')}{' '}
                <span className="font-semibold">
                  {price.lifetime.amount}
                  {price.lifetime.currency}
                </span>{' '}
                {t('pricing.price.lifetime')}
              </div>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="px-10 flex flex-col flex-1">
        <p>{description}</p>
        <ul className="mt-4 mx-4 space-y-3 flex-1">
          {features.map((feature) => (
            <li key={feature.text} className="flex items-center gap-3">
              <Icon
                name={feature.isAvailable ? 'check' : 'pending'}
                className={feature.isAvailable ? '' : 'text-gray-400'}
              />
              <span className={feature.isAvailable ? '' : 'text-gray-500'}>
                {feature.text}
                {feature.isComingSoon && ` ${t('pricing.features.future')}`}
              </span>
            </li>
          ))}
        </ul>

        {/* Hint: Prefer Google email for Stripe checkout (Premium) */}
        {price && (
          <p className="mt-8 text-md font-bold text-center">
            {t('pricing.checkout.googleEmailHint')}
          </p>
        )}

        {/* Boutons */}
        <div className="flex flex-col gap-3 items-center h-[15rem] justify-end">
          {!price && onDownload && (
            <Button
              variant="filled"
              onClick={onDownload}
              className="w-full flex-1 flex justify-center text-xl max-h-[5rem] bg-md-secondary-container"
            >
              {t('pricing.plans.free.button')}
            </Button>
          )}

          {price && (
            <div className="flex flex-col gap-4 w-[90%] h-[10rem]">
              {onSubscribe && (
                <Button
                  variant={isHighlighted ? 'filled' : 'outlined'}
                  onClick={() => {
                    const url = isAnnual 
                      ? 'https://buy.stripe.com/28E00k4H4bGy1LY2npeAg02'
                      : 'https://buy.stripe.com/3cI9AU6Pc5ia8am8LNeAg01';
                    window.location.href = url;
                  }}
                  className="w-full flex-1 flex justify-center text-xl max-h-[5rem] bg-md-secondary-container"
                >
                  {t('pricing.plans.premium.buttonSubscribe')}
                </Button>
              )}

              {onLifetime && price.lifetime && (
                <Button
                  variant="outlined"
                  onClick={() => window.location.href = 'https://buy.stripe.com/6oU14o3D0fWO8am6DFeAg00'}
                  className="w-full flex-1 flex justify-center text-xl max-h-[5rem] text-md-on-primary hover:border-md-outline"
                >
                  {t('pricing.plans.premium.buttonLifetime')} (
                  {price.lifetime.amount}
                  {price.lifetime.currency})
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
